import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import MobileFormContentManager from "../../components/common/mobile/MobileFormContentManager";
import * as designMeta from "../../api/design/meta";
import DesignTreePicker from "../../components/design/DesignTreePicker";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import MobilePeopleCellLoader from "../../components/common/people/MobilePeopleCellLoader";
import { getDocPersons } from "../../api/papers";

export default function Dcn_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const location = useLocation();
  const reasonEditorRef = useRef(null);
  const me = JSON.parse(localStorage.getItem("user"));
  const myDeptFull = me ? `${me.company}(${me.department})` : "";

  const [requestPeople, setRequestPeople] = useState([]);

  const [form, setForm] = useState({
    title: "",
    issueDept: myDeptFull,
    requestDept: myDeptFull,
    reason: "",
    details: [
      { id: 1, drawingNo: "", version: "", changeDesc: "", file: null },
    ],
  });

  // 수정 모드: meta 로드
  useEffect(() => {
    if (!paramDns) return;

    const load = async () => {
      try {
        const res = await designMeta.getMeta({ dns: paramDns });
        const meta = res.meta || res.data?.meta || {};

        const loadedPeople = await getDocPersons(paramDns);

        setDns(paramDns);
        setRequestPeople(loadedPeople);

        setForm({
          title: meta.title || "",
          requestDept: meta.requestDept || "",
          reason: meta.reason || "",
          attachments: meta.attachments || [],
          details: meta.details || [],
          alert: meta.alert || {
            send: true,
            urgent: false,
            departments: (loadedPeople || []).map((p) => p.id),
          },
        });
      } catch (e) {
        console.error(e);
        alert("기존 문서를 불러오지 못했습니다.");
      }
    };

    load();
  }, [paramDns]);

  const [dns, setDns] = useState(paramDns || "");
  const [linkedFcn, setLinkedFcn] = useState(null);
  const [fcnList, setFcnList] = useState([]);
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const getBody = useCallback(() => {
    const reasonHtml = reasonEditorRef.current?.getContent() || "";

    const validRequestPeople = requestPeople.filter(
      (p) => p && (p.user || p.id)
    );

    return {
      values: {
        project: projectId,
        module: "design",
        doc_type: "DCN",
        dns,
        related_dns: linkedFcn?.doc_dns || null,

        title: form.title,

        meta: {
          title: form.title,
          issueDept: form.issueDept,
          requestDept: form.requestDept,
          requestDeptList: form.requestDeptList || [],
          reason: reasonHtml || form.reason,

          requestPeople: validRequestPeople.map((p) => p.id),

          details: form.details.map((d, idx) => ({
            id: d.id || idx + 1,
            drawingNo: d.drawingNo || "",
            version: d.version || "",
            changeDesc: d.changeDesc || "",
            drawingFileId: d.drawingFileId || null,
            drawingFileUrl: d.drawingFileUrl || "",
            file: d.file || null,
          })),

          linkedFcn: linkedFcn?.doc_dns || null,
          attachments: form.attachments || [],

          alert: {
            send: true,
            urgent: false,
            departments: validRequestPeople.map((p) => p.user?.id || p.id),
          },
        },
      },
    };
  }, [form, dns, linkedFcn, projectId, requestPeople]);

  // FCN에서 넘어온 경우
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fcn_dns = params.get("fromFCN");
    if (fcn_dns) loadFcn(fcn_dns);
  }, [location.search]);

  useEffect(() => {
    const fetchFcnList = async () => {
      try {
        const res = await designMeta.getMetaMyList({
          project: projectId,
          doc_type: "FCN",
        });
        setFcnList(Array.isArray(res.results) ? res.results : []);
      } catch (err) {
        console.error("FCN 목록 불러오기 실패:", err);
      }
    };
    fetchFcnList();
  }, [projectId]);

  const loadFcn = async (fcn_dns) => {
    try {
      const res = await designMeta.getMeta({ dns: fcn_dns });
      const fcn = res.data || res;
      setLinkedFcn(fcn);

      const meta = fcn.meta || {};

      setForm((prev) => ({
        ...prev,

        title: meta.title ? `${meta.title} 변경도면` : "설계변경도면",

        requestDept: meta.requestDept || "",
        requestDeptList: meta.requestDeptList || [],

        reason: meta.reason || "FCN 통보에 따른 변경도면 발행.",

        details: (meta.details || []).map((d, i) => ({
          id: i + 1,
          drawingNo: d.item || d.drawingNo || "",
          drawingFileId: d.drawingFileId || null,
          drawingFileUrl: d.drawingFileUrl || "",
          version: "",
          changeDesc: `${d.after || "-"} 변경 반영`,
          file: null,
        })),
      }));
    } catch (err) {
      console.error("FCN 불러오기 실패:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.details];
      updated[index][key] = value;
      return { ...prev, details: updated };
    });
  };

  const addDetailRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: prev.details.length + 1,
          drawingNo: "",
          version: "",
          changeDesc: "",
          file: null,
        },
      ],
    }));

  const removeDetailRow = (i) =>
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        <MobileFormContentManager
          params={{
            module: "design",
            doc_type: "DCN",
            project: projectId.trim(),
            dns,
            related_dns: linkedFcn?.doc_dns || null,
          }}
          getBody={getBody}
          onChangeId={(newDns) => setDns(newDns)}
          redirectTo={`/project/${projectId}/design/list`}
          docName={"설계변경도면 (DCN)"}
          docDescribe={"도면별 변경내용과 파일을 첨부하여 발행하세요."}
          requestPeople={requestPeople}
          hideBackButton={true}
          summaryConfig={{
            title: "도면 발행 요약",
            sections: [
              { label: "제목", value: form.title },
              {
                label: "검토 담당자",
                value:
                  requestPeople.map((r) => r.user?.name || r.name).join(", ") ||
                  "-",
              },
            ],
            list: [
              {
                label: "변경 도면",
                items:
                  form.details?.map(
                    (d) => `${d.drawingNo || "-"}: ${d.changeDesc || "-"}`
                  ) || [],
              },
            ],
          }}
        >
          <div className="space-y-2 w-full min-w-0 max-w-full overflow-x-hidden">
            {/* 연결된 FCN */}
            <div className="bg-white rounded-lg border p-2 min-w-0 overflow-x-hidden">
              <label className="text-[11px] text-gray-500 mb-0.5 block">
                연결된 FCN
              </label>
              {linkedFcn ? (
                <div className="text-xs text-gray-900 font-medium">
                  {linkedFcn.doc_dns}
                </div>
              ) : (
                <select
                  className="w-full min-w-0 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => loadFcn(e.target.value)}
                >
                  <option value="">선택하세요</option>
                  {fcnList.map((fcn) => (
                    <option key={fcn.doc_dns} value={fcn.doc_dns}>
                      {fcn.doc_dns} - {fcn.title}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* 기본 정보 */}
            <div className="bg-white rounded-lg border overflow-hidden min-w-0">
              <div className="bg-gray-50 px-2 py-1.5 border-b min-w-0 overflow-x-hidden">
                <div className="text-xs font-medium text-gray-900 min-w-0 truncate">
                  기본 정보
                </div>
              </div>
              <div className="p-2 space-y-2 min-w-0 overflow-x-hidden">
                {/* 도면 제목 */}
                <div>
                  <label
                    htmlFor="dcn-title"
                    className="text-[11px] text-gray-500 mb-0.5 block"
                  >
                    도면 제목
                  </label>
                  <input
                    id="dcn-title"
                    name="title"
                    type="text"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full min-w-0 border border-gray-200 rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="예: 구조 보강 상세 변경 도면"
                  />
                </div>

                {/* 검토 담당자 */}
                <div>
                  <label className="text-[11px] text-gray-500 mb-0.5 block">
                    검토 담당자
                  </label>
                  <div className="border border-gray-200 rounded px-2 py-1.5 bg-gray-50 min-w-0 overflow-x-hidden">
                    <MobilePeopleCellLoader
                      dns={dns}
                      value={requestPeople}
                      onChange={(list) => setRequestPeople(list)}
                    />
                  </div>
                </div>

                {/* 변경 사유 */}
                <div className="min-w-0">
                  <label className="text-[11px] text-gray-500 mb-0.5 block">
                    변경 사유
                  </label>
                  <div className="border border-gray-200 rounded overflow-hidden min-w-0">
                    <TinyMCEeditor
                      height={250}
                      initialValue={form.reason}
                      editorRef={reasonEditorRef}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 변경 도면 목록 */}
            <div className="bg-white rounded-lg border overflow-hidden min-w-0">
              <div className="bg-gray-50 px-2 py-1.5 border-b flex items-center justify-between min-w-0 overflow-x-hidden">
                <div className="text-xs font-medium text-gray-900 min-w-0 flex-1 truncate">
                  변경 도면 목록
                </div>
                <button
                  onClick={addDetailRow}
                  className="btn-create-sm flex-shrink-0 whitespace-nowrap"
                >
                  + 추가
                </button>
              </div>
              <div className="p-2 space-y-1.5 min-w-0 overflow-x-hidden">
                {form.details.map((row, i) => (
                  <div
                    key={row.id}
                    className="bg-gray-50 rounded border border-gray-200 p-1.5 space-y-1.5 min-w-0 overflow-x-hidden"
                  >
                    <div className="flex items-center justify-between mb-1 min-w-0">
                      <span className="text-[11px] font-medium text-gray-700 min-w-0 flex-1 truncate">
                        No. {i + 1}
                      </span>
                      <button
                        onClick={() => removeDetailRow(i)}
                        className="btn-delete-sm flex-shrink-0 whitespace-nowrap ml-1.5"
                      >
                        삭제
                      </button>
                    </div>

                    {/* 도면번호 */}
                    <div>
                      <label className="text-[11px] text-gray-500 mb-0.5 block">
                        도면번호
                      </label>
                      {row.drawingFileId ? (
                        <div className="flex items-center justify-between w-full border border-gray-200 rounded px-2 py-1 bg-white min-w-0">
                          <span className="truncate flex-1 min-w-0 text-xs">
                            {row.drawingNo}
                          </span>
                          <button
                            onClick={() => {
                              handleDetailChange(i, "drawingFileId", null);
                              handleDetailChange(i, "drawingFileUrl", "");
                              handleDetailChange(i, "drawingNo", "");
                            }}
                            className="ml-1.5 text-red-500 hover:text-red-600 text-[10px] flex-shrink-0"
                            title="선택 해제"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <input
                            type="text"
                            value={row.drawingNo}
                            onChange={(e) =>
                              handleDetailChange(i, "drawingNo", e.target.value)
                            }
                            className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="도면번호 또는 선택"
                          />
                          <button
                            onClick={() => {
                              setActiveRow(i);
                              setShowDesignPicker(true);
                            }}
                            className="px-1.5 py-1 border border-gray-200 rounded text-[11px] hover:bg-gray-100 transition-colors flex-shrink-0 whitespace-nowrap"
                            title="설계도서에서 선택"
                          >
                            📂
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 버전 / 변경 내용 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 min-w-0">
                      <div className="min-w-0">
                        <label className="text-[11px] text-gray-500 mb-0.5 block">
                          버전
                        </label>
                        <input
                          type="text"
                          value={row.version}
                          onChange={(e) =>
                            handleDetailChange(i, "version", e.target.value)
                          }
                          className="w-full min-w-0 border border-gray-200 rounded px-1.5 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-[11px] text-gray-500 mb-0.5 block">
                          변경 내용
                        </label>
                        <input
                          type="text"
                          value={row.changeDesc}
                          onChange={(e) =>
                            handleDetailChange(i, "changeDesc", e.target.value)
                          }
                          className="w-full min-w-0 border border-gray-200 rounded px-1.5 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* 도면 파일 */}
                    <div>
                      <label className="text-[11px] text-gray-500 mb-0.5 block">
                        도면 파일
                      </label>
                      {!row.file ? (
                        <label className="cursor-pointer w-full border border-gray-200 rounded px-2 py-1.5 bg-white hover:bg-gray-50 transition-colors text-xs text-gray-700 flex items-center justify-center min-w-0">
                          파일 선택
                          <input
                            type="file"
                            onChange={(e) =>
                              handleDetailChange(
                                i,
                                "file",
                                e.target.files[0] || null
                              )
                            }
                            className="hidden"
                          />
                        </label>
                      ) : (
                        <div className="flex items-center justify-between w-full border border-gray-200 rounded px-2 py-1 bg-white min-w-0">
                          <span className="text-xs text-gray-600 truncate flex-1 min-w-0">
                            {row.file.name}
                          </span>
                          <button
                            onClick={() => handleDetailChange(i, "file", null)}
                            className="ml-1.5 text-red-500 hover:text-red-600 text-[10px] flex-shrink-0"
                            title="파일 삭제"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {linkedFcn && (
              <div className="mt-4 text-sm text-gray-600">
                ※ 본 문서는{" "}
                <a
                  href={`/project/${projectId}/design/fcn/${linkedFcn.doc_dns}`}
                  className="text-indigo-600 underline hover:text-indigo-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FCN ({linkedFcn.doc_dns})
                </a>{" "}
                통보서를 기반으로 작성되었습니다.
              </div>
            )}
          </div>

          {showDesignPicker && (
            <DesignTreePicker
              open={showDesignPicker}
              onClose={() => setShowDesignPicker(false)}
              onConfirm={(file) => {
                handleDetailChange(
                  activeRow,
                  "drawingNo",
                  file.name || file.title
                );
                handleDetailChange(activeRow, "drawingFileId", file.id);
                handleDetailChange(
                  activeRow,
                  "drawingFileUrl",
                  file.file_url || file.url || ""
                );
              }}
            />
          )}
        </MobileFormContentManager>
      </div>
    </div>
  );
}
